import { syncTime, setDrift, setDriftFromSample, getDrift } from "./services/timeService";
import {
  saveFlightData,
  saveCurrentLayout,
  saveSchedules,
  getCurrentLayout,
} from "./services/offlineStore";
import { cacheLayoutUrl } from "./sw-register";
import "./App.css";
import { useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import HtmlViewer from "./components/HtmlViewer";
import { SOCKET_PATH } from "./config/serverConfig";
import { useScheduleManager } from "./hooks/useScheduleManager";
import { usePriorityResolver } from "./hooks/usePriorityResolver";
import { LicenseExpiredOverlay, LicenseWarningBanner } from "./components/LicenseOverlay";
import axios from "axios";
import PairDevice from "./components/PairDevice";
import ServerDown from "./components/ServerDown";
import { Box, CircularProgress, Typography } from "@mui/material";
import { FullRectangleLogo } from "./assets/images/logos";

function App() {
  const serverUrl = import.meta.env.VITE_SERVER_BASE_URL;
  // ─── MEMORY LEAK FIX: Compute target origin once for postMessage security ───
  const serverOrigin = (() => {
    try {
      return new URL(serverUrl).origin;
    } catch {
      return window.location.origin;
    }
  })();
  const [view, setView] = useState(null);
  console.log("App view:", view);
  const [socketUrl, setSocketUrl] = useState("");
  const [socketData, setSocketData] = useState({
    socketError: null,
    serverDown: false,
  });
  const iframeRef = useRef(null);
  const fullScreenRef = useRef(null);
  const socketRef = useRef(null);
  const currentUrlRef = useRef(null);
  // Set when the server deliberately unpairs this device (emits "delete" or a
  // rejection "error"). Once unpaired, the socket's "disconnect" handler must
  // NOT auto-reconnect — otherwise a one-time "you've been deleted, go pair"
  // turns into an infinite reconnect + re-pair loop that trips the
  // /pairing/start rate limit (429). Reset to false on a fresh connect.
  const unpairedRef = useRef(false);
  // Grace timer: a transient drop (common on Android TV WebViews) should NOT
  // immediately show the full-screen "Connection Error" overlay while the
  // socket is already auto-reconnecting. Only surface the error if we're still
  // disconnected after this window.
  const connErrorTimerRef = useRef(null);
  const CONNECTION_ERROR_GRACE_MS = 30000;

  const clearConnErrorTimer = () => {
    if (connErrorTimerRef.current) {
      clearTimeout(connErrorTimerRef.current);
      connErrorTimerRef.current = null;
    }
  };

  // Called on any connection hiccup. Starts the grace timer (once) instead of
  // showing the error immediately; the timer only fires if still offline.
  const scheduleConnError = (message) => {
    if (connErrorTimerRef.current) return; // already counting down
    connErrorTimerRef.current = setTimeout(() => {
      connErrorTimerRef.current = null;
      if (!socketRef.current?.connected) {
        setSocketData({ socketError: message, serverDown: true });
        setSchedules([]);
      }
    }, CONNECTION_ERROR_GRACE_MS);
  };

  const emitLifecycle = (state, reason = null) => {
    const socket = socketRef.current;
    if (!socket?.connected) return;

    socket.emit("device.lifecycle", {
      state,
      reason,
      deviceId: localStorage.getItem("deviceId"),
      location: "Not Available",
      visibilityState: document.visibilityState,
      isOnline: navigator.onLine,
      clientNow: Date.now(),
    });
  };

  // Centralized clock + scheduling
  const [serverTimeOffsetMs, setServerTimeOffsetMs] = useState(0);
  const [schedules, setSchedules] = useState([]);
  const [overrideActive, setOverrideActive] = useState(null);
  const [deviceId, setDeviceId] = useState(null);

  // License status state (received via socket events)
  const [licenseStatus, setLicenseStatus] = useState(null);
  const licenseExpired = licenseStatus?.status === "expired";
  const licenseWarning =
    licenseStatus?.warning === "warning" || licenseStatus?.warning === "critical";
  const licenseCritical = licenseStatus?.warning === "critical";
  const licenseDaysRemaining = licenseStatus?.daysRemaining ?? null;
  const licenseExpiresOn = licenseStatus?.expiresOn ?? null;
  const licenseExpiresAt = licenseStatus?.expiresAt ?? null;
  const licenseMsRemaining = licenseStatus?.msRemaining ?? null;
  const licenseServerNow = licenseStatus?.serverNow ?? null;
  const licenseCustomerName = licenseStatus?.customerName ?? null;
  const licenseContactEmail = licenseStatus?.contact?.email ?? null;

  // Helper to send schedule status to server via socket
  const sendScheduleStatus = (scheduleId, status) => {
    const deviceId = localStorage.getItem("deviceId");
    if (socketRef.current?.connected && scheduleId && deviceId) {
      socketRef.current.emit("schedule.status", {
        scheduleId,
        status,
        deviceId,
        timestamp: Date.now(),
      });
    }
  };

  const handleScheduleActivate = (info) => {
    sendScheduleStatus(info.scheduleId, "Ongoing");
  };

  const handleScheduleComplete = (info) => {
    sendScheduleStatus(info.scheduleId, "Completed");
  };

  const { activeScheduleUrl, displayMode, validScheduleCount } = useScheduleManager({
    schedules,
    serverTimeOffsetMs,
    onScheduleActivate: handleScheduleActivate,
    onScheduleComplete: handleScheduleComplete,
  });

  // Priority resolver: Override > Scheduled > Default
  const mode = usePriorityResolver({
    overrideActive,
    scheduledUrl:
      activeScheduleUrl &&
      activeScheduleUrl +
        // ARD-50 #4: include timeOffset so a template swapped in during a
        // recurring schedule initializes the same server-drift offset the
        // default template uses — otherwise its clock shows raw local time.
        `&deviceWidth=${window.innerWidth}&deviceHeight=${window.innerHeight}&deviceId=${deviceId}&timeOffset=${getDrift()}`,
    defaultUrl: socketUrl,
  });

  // Update ref whenever the resolved URL changes
  useEffect(() => {
    currentUrlRef.current = mode.url || socketUrl;
  }, [mode.url, socketUrl]);

  const connectSocket = async () => {
    // A new connection attempt means the device is (re)pairing or starting
    // fresh — clear any prior "unpaired" state so legitimate reconnects work.
    unpairedRef.current = false;
    // ─── MEMORY LEAK FIX: Always clean up existing socket before creating new one ───
    // Prevents duplicate socket connections on re-pair (was leaking ~10MB per reconnect)
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    try {
      let deviceId;
      const existingDeviceId = localStorage.getItem("deviceId");
      if (!existingDeviceId || existingDeviceId === "null") {
        // ─── SECURITY FIX: Cryptographic device ID (replaces Math.random) ─
        deviceId =
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
        localStorage.setItem("deviceId", deviceId);
      } else {
        deviceId = existingDeviceId;
      }
      setDeviceId(deviceId);
      const locationMarker = localStorage.getItem("locationMarker");
      const terminal = localStorage.getItem("terminal");
      const zone = localStorage.getItem("zone");
      const deviceToken = localStorage.getItem("deviceToken") || "";
      // A fully-paired device has location identity AND a device token. If the
      // location keys linger from a prior setup but the token is missing (stale
      // / partially-cleared localStorage), connecting would be rejected by the
      // server ("error" → clears storage → drops to pairing), which flashes a
      // connection error on first load and only "fixes itself" on reload once
      // the storage is cleared. Require the token up front so a stale identity
      // goes straight to the pairing screen instead of a doomed connect.
      if (locationMarker && terminal && zone && deviceToken) {
        const socket = io(serverOrigin, {
          path: SOCKET_PATH,
          query: {
            deviceId: deviceId,
            location_marker: locationMarker,
            terminal,
            zone,
            width: window.innerWidth,
            height: window.innerHeight,
            deviceToken,
          },
          // 5s was too tight for Android TV boxes on slow Wi-Fi; a brief
          // latency spike tripped it. 20s tolerates a slow handshake.
          timeout: 20000,
          reconnection: true,
          reconnectionAttempts: Infinity,
          reconnectionDelay: 2000,
          reconnectionDelayMax: 30000,
        });

        // A successful reconnect clears any pending error state/overlay.
        // (The main "connect" handler below also clears it via
        // clearConnErrorTimer so both the first connect and reconnects recover.)
        socket.io.on("reconnect", () => {
          clearConnErrorTimer();
          setSocketData({ socketError: null, serverDown: false });
        });
        // Debounce: ignore rapid-fire url events (within 2 seconds)
        // but always accept if enough time has passed (handles template re-publish)
        // let _lastUrlTime = 0;
        socket.on("url", (data) => {
          clearConnErrorTimer();
          setSocketData((prev) => ({ ...prev, socketError: null, serverDown: false }));
          // const now = Date.now();
          // if (now - _lastUrlTime < 2000) return;
          // _lastUrlTime = now;
          setSocketUrl(
            data +
              `&deviceWidth=${window.innerWidth}&deviceHeight=${window.innerHeight}&deviceId=${deviceId}&timeOffset=${getDrift()}`,
          );
          // ─── PHASE 3: Persist layout URL for offline resilience ─────────
          const fullUrl =
            data +
            `&deviceWidth=${window.innerWidth}&deviceHeight=${window.innerHeight}&deviceId=${deviceId}&timeOffset=${getDrift()}`;
          saveCurrentLayout(fullUrl, deviceId);
          cacheLayoutUrl(fullUrl);
        });
        // Centralized server time (clock sync)
        socket.on("serverTime", (data) => {
          // Server-pushed timestamp (no client round-trip to measure). Route
          // through setDrift so outlier/drift guards still apply, then use the
          // service's authoritative drift for downstream consumers.
          setDrift(data.serverNow - Date.now());
          const offset = getDrift();
          setServerTimeOffsetMs(offset);
          // ARD-26: Forward time offset to iframe via postMessage (origin-restricted)
          if (iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.postMessage(
              { type: "timeSync", offsetMs: offset },
              serverOrigin,
            );
          }
        });

        // License status updates (received on connect + every 60 seconds)
        socket.on("license:status", (data) => {
          console.log("[License] Received status:", data.status, data);
          setLicenseStatus(data);
        });

        // P1-8: Remove existing listener before adding new one to prevent accumulation
        // Schedules sync (2-window scheduler rebuilds)
        socket.off("schedulesSync");
        socket.on("schedulesSync", (data) => {
          setSchedules(data.schedules || []);
          if (data.serverNow) {
            setDrift(data.serverNow - Date.now());
            const offset = getDrift();
            setServerTimeOffsetMs(offset);
            // ARD-26: Forward time offset to iframe via postMessage (origin-restricted)
            if (iframeRef.current?.contentWindow) {
              iframeRef.current.contentWindow.postMessage(
                { type: "timeSync", offsetMs: offset },
                serverOrigin,
              );
            }
          }
        });
        // Override activate (emergency)
        socket.on("override.activate", (data) => {
          const overrideUrl =
            data.url +
            // ARD-50 #4: include timeOffset so the override template's clock
            // initializes with the correct server-drift offset on load.
            `&deviceWidth=${window.innerWidth}&deviceHeight=${window.innerHeight}&deviceId=${deviceId}&timeOffset=${getDrift()}`;
          setOverrideActive({
            url: overrideUrl,
            message: data.message || null,
            severity: data.severity || null,
            type: data.type || null,
          });
          // ARD-19: Acknowledge override receipt to server
          if (data.overrideId) {
            socket.emit("override.ack", {
              overrideId: data.overrideId,
              deviceId: deviceId,
              timestamp: Date.now(),
            });
          }
          // If dynamic message is provided, inject into the override iframe after load
          if (data.message && iframeRef.current) {
            setTimeout(() => {
              try {
                iframeRef.current?.contentWindow?.postMessage(
                  { type: "emergencyMessage", message: data.message },
                  serverOrigin,
                );
              } catch (e) {}
            }, 1000);
          }
        });
        // Override clear
        socket.on("override.clear", () => {
          setOverrideActive(null);
        });
        socket.on("flight:update", (data) => {
          if (iframeRef.current) {
            iframeRef.current.contentWindow.postMessage(
              { type: "flightUpdate", data },
              serverOrigin,
            );

            // ARD-PARITY Task 15.4: Re-render dynamic text bindings in real-time
            // Query iframe for elements with data-binding attribute and update them
            try {
              iframeRef.current.contentWindow.postMessage(
                { type: "bindingUpdate", data },
                serverOrigin,
              );
            } catch (e) {
              // Silent failure for cross-origin restrictions
            }
          }
          // ─── PHASE 3: Persist flight updates for offline resilience ─────
          if (data?.direction) saveFlightData(data.direction, data);
        });
        // Debounced: only forward flightDataSynced once per 10 seconds
        let _lastFlightSync = 0;
        socket.on("flight:dataSynced", (data) => {
          const now = Date.now();
          if (now - _lastFlightSync < 10000) return;
          _lastFlightSync = now;
          if (iframeRef.current) {
            iframeRef.current.contentWindow.postMessage(
              { type: "flightDataSynced", data },
              serverOrigin,
            );
          }
          // ─── PHASE 3: Persist synced flight data ────────────────────────
          if (data?.direction) saveFlightData(data.direction, data);
        });
        // News Widget: forward news updates to iframe
        socket.on("news:update", (data) => {
          if (iframeRef.current) {
            iframeRef.current.contentWindow.postMessage({ type: "newsUpdate", data }, serverOrigin);
          }
        });

        // ARD-PARITY Task 15.2: Listen for cache invalidation on template publish
        // When a template is published, clear service worker cache and reload
        socket.on("template:invalidate", async ({ templateId, version, timestamp }) => {
          // Extract current template ID from the socketUrl
          const currentTemplateId = currentUrlRef.current?.match(/get-html\/([^&?]+)/)?.[1];

          // Only act if this is the template currently displayed
          if (currentTemplateId && currentTemplateId === templateId) {
            console.log(
              `[CacheInvalidate] Template ${templateId} updated, clearing cache and reloading`,
            );

            // Clear service worker cache
            if ("caches" in window) {
              try {
                const cacheNames = await caches.keys();
                await Promise.all(cacheNames.map((name) => caches.delete(name)));
              } catch (e) {
                console.warn("[CacheInvalidate] Failed to clear caches:", e);
              }
            }

            // The "url" event will be emitted separately by the server to trigger reload
            // This cache clear ensures the reload fetches fresh content
          }
        });

        socket.on("delete", (data) => {
          console.log("Device deleted remotely", data);
          // Deliberate unpair — stop the socket from auto-reconnecting (the
          // accompanying "io server disconnect" must not resurrect it).
          unpairedRef.current = true;
          socket.removeAllListeners();
          socket.disconnect();
          localStorage.removeItem("locationMarker");
          localStorage.removeItem("deviceId");
          localStorage.removeItem("terminal");
          localStorage.removeItem("zone");
          localStorage.removeItem("deviceToken");
          setView("pair");
        });

        // Server-side rejection (emitted as "error" then the socket is
        // disconnected): license expired/missing, display capacity exceeded,
        // invalid device token, or a stale/not-in-DB identity whose zone/terminal
        // no longer resolve. Previously this was unhandled, so a device carrying
        // leftover localStorage from a prior setup stayed stuck on the content
        // screen ("Content failed to load") forever. Treat it like "delete":
        // clear the stale identity and drop to the pairing screen so the device
        // can be reconfigured. (Transient server-unreachable cases surface via
        // "connect_error" below, not "error", so this won't unpair on blips.)
        socket.on("error", (data) => {
          console.warn("Device connection rejected by server:", data);
          // Deliberate rejection — don't auto-reconnect (see "delete" above).
          unpairedRef.current = true;
          socket.removeAllListeners();
          socket.disconnect();
          localStorage.removeItem("locationMarker");
          localStorage.removeItem("deviceId");
          localStorage.removeItem("terminal");
          localStorage.removeItem("zone");
          localStorage.removeItem("deviceToken");
          setView("pair");
        });

        // Handle preview request from admin
        socket.on("preview.request", (callback) => {
          try {
            // Use ref to get current URL (avoids stale closure)
            const currentUrl = currentUrlRef.current;
            if (currentUrl && typeof callback === "function") {
              callback({ url: currentUrl });
            } else {
              callback({ error: "Device not ready - no URL loaded yet" });
            }
          } catch (error) {
            console.error("Preview request error:", error);
            if (typeof callback === "function") {
              callback({ error: error.message });
            }
          }
        });
        socket.on("connect", () => {
          clearConnErrorTimer();
          setSocketData({ socketError: null, serverDown: false });
          emitLifecycle("online", "socket_connected");
          socket.emit("device.heartbeat", {
            deviceId: localStorage.getItem("deviceId"),
            location: "Not Available",
            lifecycleState: "online",
            visibilityState: document.visibilityState,
            isOnline: navigator.onLine,
            clientNow: Date.now(),
          });
          // Self-heal after any (re)connect: pull the current template URL in
          // case a publish happened while this display was briefly offline
          // (sleep / network drop). Server-pushed "url" room-emits are
          // fire-and-forget and can be missed during the reconnect window, so
          // we proactively request the latest. The ack also drives the reload
          // directly; the server additionally emits "url" for the normal path.
          socket.emit(
            "requestCurrentUrl",
            { deviceId: localStorage.getItem("deviceId") },
            (resp) => {
              if (resp && resp.url) {
                setSocketData((prev) => ({ ...prev, socketError: null }));
                const fullUrl =
                  resp.url +
                  `&deviceWidth=${window.innerWidth}&deviceHeight=${window.innerHeight}&deviceId=${deviceId}&timeOffset=${getDrift()}`;
                setSocketUrl(fullUrl);
                saveCurrentLayout(fullUrl, deviceId);
                cacheLayoutUrl(fullUrl);
              }
            },
          );
          setView("html");
        });
        // General connection error (server unreachable). Don't show the overlay
        // instantly — the client is already auto-reconnecting. Start the grace
        // timer; it only surfaces the error if we're still offline after it.
        socket.on("connect_error", (err) => {
          console.log("Connect error (will retry quietly):", err?.message);
          scheduleConnError(err?.message || "Connection error");
        });

        // Handle disconnect - attempt reconnection for license recovery
        socket.on("disconnect", (reason) => {
          console.log("[Socket] Disconnected:", reason);
          // Transient disconnect — let auto-reconnect work; only surface the
          // overlay if it doesn't recover within the grace window.
          scheduleConnError(`Disconnected: ${reason}`);

          // If server initiated disconnect (license expired), attempt reconnect after delay
          // This allows device to reconnect after license renewal + server restart.
          // BUT NOT after a deliberate unpair ("delete"/"error") — those also
          // disconnect with reason "io server disconnect", and reconnecting
          // would resurrect a device the server just removed, causing an
          // infinite reconnect + re-pair loop that trips /pairing/start (429).
          if (
            !unpairedRef.current &&
            (reason === "io server disconnect" || reason === "transport close")
          ) {
            console.log("[Socket] Server-initiated disconnect - will attempt reconnect in 10s");
            setTimeout(() => {
              if (unpairedRef.current) return; // unpaired meanwhile — abort
              console.log("[Socket] Attempting reconnection after license disconnect...");
              if (!socket.connected) {
                socket.connect();
              }
            }, 10000); // Wait 10 seconds before trying to reconnect
          }
        });

        // Store socket reference for Heartbeat
        socketRef.current = socket;
      } else {
        setView("pair");
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  useEffect(() => {
    connectSocket();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        // Esc drops to the pairing screen only when the device isn't already
        // paired. A paired, running display must not be unpaired by a stray
        // Esc press — reconfiguration is done from the admin panel / a fresh
        // pairing session on the device, not an accidental keypress.
        const isPaired =
          localStorage.getItem("locationMarker") &&
          localStorage.getItem("terminal") &&
          localStorage.getItem("zone");
        if (!isPaired) {
          setView("pair");
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      // Disconnect socket
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    const heartbeatInterval = setInterval(() => {
      const socket = socketRef.current;
      const storedDeviceId = localStorage.getItem("deviceId");
      // ─── MEMORY LEAK FIX: Skip heartbeat if socket or deviceId unavailable ───
      if (!socket?.connected || !storedDeviceId) return;
      socket.emit("device.heartbeat", {
        deviceId: storedDeviceId,
        location: "Not Available",
        lifecycleState: document.visibilityState === "hidden" ? "hidden" : "visible",
        visibilityState: document.visibilityState,
        isOnline: navigator.onLine,
        clientNow: Date.now(),
      });
    }, 30000);

    return () => clearInterval(heartbeatInterval);
  }, []);

  // P2-6: Periodic clock re-sync (ARD-26: configurable interval, HTTP fallback)
  useEffect(() => {
    const resyncInterval = setInterval(
      () => {
        const socket = socketRef.current;
        if (socket?.connected) {
          // ARD-26: measure round-trip so offset is latency-compensated.
          const t0 = Date.now();
          socket.emit("requestServerTime", {}, (response) => {
            if (response?.serverNow) {
              const t3 = Date.now();
              setDriftFromSample(response.serverNow, t0, t3);
              const offset = getDrift();
              setServerTimeOffsetMs(offset);
              // ARD-26: Forward updated offset to iframe (origin-restricted)
              if (iframeRef.current?.contentWindow) {
                iframeRef.current.contentWindow.postMessage(
                  { type: "timeSync", offsetMs: offset },
                  serverOrigin,
                );
              }
            }
          });
        } else {
          // ARD-26: HTTP fallback when socket disconnected
          syncTime().then((offset) => {
            setServerTimeOffsetMs(offset);
            if (iframeRef.current?.contentWindow) {
              iframeRef.current.contentWindow.postMessage(
                { type: "timeSync", offsetMs: offset },
                serverOrigin,
              );
            }
          });
        }
      },
      60 * 1000, // 60 seconds (configurable via server in future)
    );

    return () => clearInterval(resyncInterval);
  }, []);

  const goFullscreen = async () => {
    if (fullScreenRef.current && document.fullscreenEnabled) {
      try {
        await (fullScreenRef.current.requestFullscreen?.() ||
          fullScreenRef.current.webkitRequestFullscreen?.() ||
          fullScreenRef.current.mozRequestFullScreen?.() ||
          fullScreenRef.current.msRequestFullscreen?.() ||
          Promise.resolve());
      } catch (_) {
        // Fullscreen requires user gesture — will be triggered on first interaction
      }
    }
  };

  // Attempt fullscreen on view change (may fail without user gesture)
  useEffect(() => {
    if (view === "html") {
      goFullscreen();
      // Fallback: enter fullscreen on first user click/tap
      const handleInteraction = () => {
        goFullscreen();
        document.removeEventListener("click", handleInteraction);
      };
      document.addEventListener("click", handleInteraction);
      return () => document.removeEventListener("click", handleInteraction);
    }
  }, [view]);

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  // ─── MEMORY LEAK FIX: Consolidated online/offline + lifecycle listeners ─────
  // Previously had duplicate online/offline handlers causing redundant event firing
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      emitLifecycle("online_browser", "browser_online_event");
    };

    const handleOffline = () => {
      setIsOnline(false);
      emitLifecycle("offline_browser", "browser_offline_event");
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        emitLifecycle("hidden", "visibility_hidden");
      } else {
        emitLifecycle("visible", "visibility_visible");
      }
    };

    const onPageHide = () => {
      emitLifecycle("pagehide_unload", "pagehide");
    };

    const onBeforeUnload = () => {
      emitLifecycle("shutdown_intent", "beforeunload");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, []);

  return (
    <div ref={fullScreenRef} className="App">
      {/* License Expired: Full blocking overlay */}
      {licenseExpired && (
        <LicenseExpiredOverlay
          customerName={licenseCustomerName}
          contactEmail={licenseContactEmail}
        />
      )}

      {/* License Warning Banner (only when not expired) */}
      {!licenseExpired && licenseWarning && licenseDaysRemaining !== null && (
        <LicenseWarningBanner
          daysRemaining={licenseDaysRemaining}
          expiresOn={licenseExpiresOn}
          expiresAt={licenseExpiresAt}
          msRemaining={licenseMsRemaining}
          serverNow={licenseServerNow}
          isCritical={licenseCritical}
          position="top"
        />
      )}

      {view === "html" ? (
        <HtmlViewer
          url={mode.url || socketUrl}
          setView={setView}
          iframeRef={iframeRef}
          emergencyMessage={overrideActive?.message || null}
        />
      ) : view === "pair" ? (
        <PairDevice
          setView={setView}
          ipAddress={serverUrl}
          socketOrigin={serverOrigin}
          onConfigurationComplete={connectSocket}
        />
      ) : (
        <Box
          sx={{
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          }}
        >
          <Box
            component="img"
            src={FullRectangleLogo}
            alt="D-hive.ai"
            sx={{ width: { xs: 220, md: 320 }, maxWidth: "80%", objectFit: "contain", mb: 3 }}
          />
          <CircularProgress size={60} sx={{ color: "#2196F3" }} />
          <Typography sx={{ mt: 2, color: "#546e7a" }}>Starting your device...</Typography>
        </Box>
      )}
      {(!isOnline || socketData.serverDown) && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            right: 0,
            padding: "5px",
            backgroundColor: "black",
            color: "yellow",
            fontSize: "clamp(12px, .8vw + .6vh, 32px)",
          }}
        >
          ⚠️ Displaying last known data{" "}
          {!isOnline ? `(Not connected to network)` : `(Server Offline)`}
        </div>
      )}
    </div>
  );
}

export default App;
